/**
 * OrgCompanyProfile — shared, self-contained company-profile editor.
 *
 * One source of truth for the firmographic profile used by BOTH:
 *   - Consultant Client Profile (orgId = the client's org)
 *   - Contractor Workspace Settings (orgId = the contractor's own org)
 *
 * A contractor's org is type CLIENT, and the backend allows a client-side
 * user to self-edit their own org, so the same /api/clients/{orgId}
 * endpoints work for both. Loads GET /api/clients/{orgId}, saves via PUT.
 */
import { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Separator } from '../ui/separator';
import { Building2, Save, Loader2, Globe, Sparkles } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const csv = (arr) => (Array.isArray(arr) ? arr.join(', ') : (arr || ''));
const splitCsv = (s) => (s || '').split(',').map((x) => x.trim()).filter(Boolean);

export default function OrgCompanyProfile({ orgId, title = 'Company Profile' }) {
  const { authToken } = useAuth();
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [scanning, setScanning] = useState(false);

  const auth = { Authorization: `Bearer ${authToken}` };

  const load = useCallback(async () => {
    if (!orgId || !authToken) return;
    try {
      const res = await fetch(`${API_URL}/api/clients/${orgId}`, { headers: auth });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const d = await res.json();
      setForm({
        name: d.name || '',
        uei: d.uei || '',
        cage_code: d.cage_code || '',
        duns: d.duns || '',
        website: d.website || '',
        phone: d.phone || '',
        address_line1: d.address_line1 || '',
        address_line2: d.address_line2 || '',
        city: d.city || '',
        state: d.state || '',
        zip_code: d.zip_code || '',
        business_size: d.business_size || '',
        socioeconomic_status: d.socioeconomic_status || '',
        primary_naics: d.primary_naics || '',
        target_naics_csv: csv(d.target_naics_codes),
        target_psc_csv: csv(d.target_psc_codes),
        industry_sector: d.industry_sector || '',
        ebuy_client_code: d.ebuy_client_code || '',
        capabilities_summary: d.capabilities_summary || '',
        brand_colors: d.brand_colors || null,
      });
    } catch (e) {
      toast.error('Failed to load company profile');
      setForm({});
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orgId, authToken]);

  useEffect(() => { load(); }, [load]);

  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const handleScanWebsite = async () => {
    if (!(form?.website || '').trim()) { toast.error('Enter a website URL first'); return; }
    setScanning(true);
    try {
      const res = await fetch(`${API_URL}/api/clients/${orgId}/scan-website`, {
        method: 'POST',
        headers: { ...auth, 'Content-Type': 'application/json' },
        body: JSON.stringify({ website: form.website.trim() }),
      });
      const d = await res.json().catch(() => ({}));
      if (!res.ok) { toast.error(d.detail || 'Scan failed'); return; }
      setForm((p) => ({
        ...p,
        brand_colors: d.brand_colors || p.brand_colors,
        capabilities_summary: d.capabilities_summary || p.capabilities_summary,
      }));
      toast.success('Website scanned — brand colors & capabilities updated');
    } catch (e) {
      toast.error(`Scan error: ${e.message}`);
    } finally {
      setScanning(false);
    }
  };

  const handleSave = async () => {
    if (!(form.name || '').trim()) { toast.error('Company name is required'); return; }
    setSaving(true);
    try {
      const res = await fetch(`${API_URL}/api/clients/${orgId}`, {
        method: 'PUT',
        headers: { ...auth, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: form.name,
          uei: form.uei || null,
          cage_code: form.cage_code || null,
          duns: form.duns || null,
          website: form.website || null,
          phone: form.phone || null,
          address_line1: form.address_line1 || null,
          address_line2: form.address_line2 || null,
          city: form.city || null,
          state: form.state || null,
          zip_code: form.zip_code || null,
          business_size: form.business_size || null,
          socioeconomic_status: form.socioeconomic_status || null,
          primary_naics: form.primary_naics || null,
          target_naics_codes: splitCsv(form.target_naics_csv),
          target_psc_codes: splitCsv(form.target_psc_csv),
          industry_sector: form.industry_sector || null,
          ebuy_client_code: (form.ebuy_client_code || '').trim().toUpperCase() || null,
          capabilities_summary: form.capabilities_summary || null,
          brand_colors: form.brand_colors || null,
        }),
      });
      if (!res.ok) {
        const d = await res.json().catch(() => ({}));
        throw new Error(d.detail || `HTTP ${res.status}`);
      }
      toast.success('Company profile saved');
      await load();
    } catch (e) {
      toast.error(e.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  if (form === null) {
    return (
      <Card><CardContent className="py-10 flex justify-center">
        <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
      </CardContent></Card>
    );
  }

  const palette = form.brand_colors || {};
  const swatches = ['primary', 'secondary', 'accent', 'background', 'text'].filter((k) => palette[k]);

  return (
    <Card className="border-slate-200" data-testid="org-company-profile">
      <CardHeader className="pb-3">
        <CardTitle className="text-base flex items-center gap-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
          <Building2 className="w-5 h-5 text-slate-500" /> {title}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">

        {/* Organization */}
        <div className="space-y-3">
          <h4 className="text-sm font-semibold text-slate-900">Organization</h4>
          <div className="grid grid-cols-2 gap-4">
            <div className="col-span-2">
              <Label>Company Name *</Label>
              <Input value={form.name} onChange={(e) => set('name', e.target.value)} className="mt-1" data-testid="org-name" />
            </div>
            <div><Label>UEI</Label>
              <Input value={form.uei} onChange={(e) => set('uei', e.target.value)} className="mt-1 font-mono" maxLength={12} /></div>
            <div><Label>CAGE Code</Label>
              <Input value={form.cage_code} onChange={(e) => set('cage_code', e.target.value)} className="mt-1 font-mono" maxLength={5} /></div>
            <div><Label>Business Size</Label>
              <Input value={form.business_size} onChange={(e) => set('business_size', e.target.value)} className="mt-1" placeholder="Small / Large" /></div>
            <div><Label>Socio-economic Status</Label>
              <Input value={form.socioeconomic_status} onChange={(e) => set('socioeconomic_status', e.target.value)} className="mt-1" placeholder="SDVOSB, 8(a), WOSB…" /></div>
            <div className="col-span-2"><Label>Industry Sector</Label>
              <Input value={form.industry_sector} onChange={(e) => set('industry_sector', e.target.value)} className="mt-1" /></div>
          </div>
        </div>

        <Separator />

        {/* Codes */}
        <div className="space-y-3">
          <h4 className="text-sm font-semibold text-slate-900">NAICS / PSC / eBuy</h4>
          <div className="grid grid-cols-2 gap-4">
            <div><Label>Primary NAICS</Label>
              <Input value={form.primary_naics} onChange={(e) => set('primary_naics', e.target.value)} className="mt-1 font-mono" maxLength={6} /></div>
            <div><Label>eBuy Folder Code</Label>
              <Input value={form.ebuy_client_code}
                onChange={(e) => set('ebuy_client_code', e.target.value.toUpperCase().slice(0, 3))}
                className="mt-1 font-mono uppercase" maxLength={3} placeholder="CEC" /></div>
            <div className="col-span-2"><Label>Target NAICS Codes <span className="text-slate-400 text-xs">(comma-separated)</span></Label>
              <Input value={form.target_naics_csv} onChange={(e) => set('target_naics_csv', e.target.value)} className="mt-1 font-mono" placeholder="541611, 541512" /></div>
            <div className="col-span-2"><Label>Target PSC Codes <span className="text-slate-400 text-xs">(comma-separated)</span></Label>
              <Input value={form.target_psc_csv} onChange={(e) => set('target_psc_csv', e.target.value)} className="mt-1 font-mono" placeholder="D302, R425" /></div>
          </div>
        </div>

        <Separator />

        {/* Contact */}
        <div className="space-y-3">
          <h4 className="text-sm font-semibold text-slate-900">Contact</h4>
          <div className="grid grid-cols-2 gap-4">
            <div className="col-span-2"><Label>Website</Label>
              <div className="mt-1 flex gap-2">
                <Input value={form.website} onChange={(e) => set('website', e.target.value)} placeholder="https://…" />
                <Button type="button" variant="outline" onClick={handleScanWebsite} disabled={scanning}
                  className="gap-1.5 flex-shrink-0 border-slate-300" title="Scan site for brand colors & capabilities">
                  {scanning ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />} Scan
                </Button>
              </div>
            </div>
            <div><Label>Phone</Label>
              <Input value={form.phone} onChange={(e) => set('phone', e.target.value)} className="mt-1" /></div>
            <div><Label>DUNS</Label>
              <Input value={form.duns} onChange={(e) => set('duns', e.target.value)} className="mt-1 font-mono" /></div>
            <div className="col-span-2"><Label>Address Line 1</Label>
              <Input value={form.address_line1} onChange={(e) => set('address_line1', e.target.value)} className="mt-1" /></div>
            <div className="col-span-2"><Label>Address Line 2</Label>
              <Input value={form.address_line2} onChange={(e) => set('address_line2', e.target.value)} className="mt-1" /></div>
            <div><Label>City</Label>
              <Input value={form.city} onChange={(e) => set('city', e.target.value)} className="mt-1" /></div>
            <div className="grid grid-cols-2 gap-2">
              <div><Label>State</Label>
                <Input value={form.state} onChange={(e) => set('state', e.target.value)} className="mt-1" maxLength={2} /></div>
              <div><Label>ZIP</Label>
                <Input value={form.zip_code} onChange={(e) => set('zip_code', e.target.value)} className="mt-1" /></div>
            </div>
          </div>
        </div>

        <Separator />

        {/* Capabilities + brand */}
        <div className="space-y-3">
          <h4 className="text-sm font-semibold text-slate-900">Capabilities</h4>
          <Textarea value={form.capabilities_summary} onChange={(e) => set('capabilities_summary', e.target.value)}
            placeholder="2–3 sentences describing what this company does and its differentiators…"
            className="min-h-[90px]" />
          {swatches.length > 0 && (
            <div className="flex items-center gap-2 pt-1">
              <span className="text-xs text-slate-500">Brand:</span>
              {swatches.map((k) => (
                <span key={k} title={`${k}: ${palette[k]}`}
                  className="w-6 h-6 rounded border border-slate-200"
                  style={{ background: palette[k] }} />
              ))}
              {palette.palette_name && <span className="text-xs text-slate-400">{palette.palette_name}</span>}
            </div>
          )}
        </div>

        <div className="flex justify-end pt-2">
          <Button onClick={handleSave} disabled={saving} className="gap-2" data-testid="org-profile-save">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            {saving ? 'Saving…' : 'Save Company Profile'}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
